// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。
import LensGlassRoot from '@components/design-system/LensGlassRoot';

import { motion } from 'framer-motion';
import { Loader2, Settings, Wrench } from 'lucide-react';

interface MaintenancePageProps {
  message?: string;
}

export default function MaintenancePage({ message }: MaintenancePageProps) {
  const displayMessage = message || '系统正在升级维护中，请耐心等待...';

  return (
    <LensGlassRoot className="flex min-h-screen items-center justify-center">
      {/* Background decorations */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-sky-200/30 blur-3xl dark:bg-sky-900/20" />
        <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-blue-200/30 blur-3xl dark:bg-blue-900/20" />
        <div className="absolute left-1/2 top-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-indigo-100/40 blur-3xl dark:bg-indigo-900/10" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="relative z-10 mx-4 max-w-lg text-center"
      >
        {/* Gear animation */}
        <motion.div
          className="mx-auto mb-8 flex h-24 w-24 items-center justify-center"
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="relative">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
              className="text-sky-400/80 dark:text-sky-500/60"
            >
              <Settings className="h-16 w-16" />
            </motion.div>
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
              className="absolute -bottom-1 -right-1 text-blue-400/60 dark:text-blue-500/40"
            >
              <Wrench className="h-10 w-10" />
            </motion.div>
          </div>
        </motion.div>

        {/* Title */}
        <h1
          className="mb-4 text-3xl font-bold tracking-tight text-slate-800 dark:text-slate-100 sm:text-4xl"
          style={{ fontFamily: '"Source Han Serif SC", "Songti SC", "STSong", serif' }}
        >
          系统升级中
        </h1>

        {/* Description */}
        <p className="mb-8 text-lg leading-relaxed text-slate-600 dark:text-slate-300">
          {displayMessage}
        </p>

        {/* Loading indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="flex items-center justify-center gap-2 text-sm text-sky-600/80 dark:text-sky-400/70"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>正在部署新版本，稍后将自动恢复</span>
        </motion.div>

        {/* Brand */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-12 text-xs text-slate-400 dark:text-slate-500"
        >
          万象AI · 数理智能教学平台
        </motion.p>

        {/* Admin login link */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="mt-3 text-xs text-slate-400/60 dark:text-slate-500/60"
        >
          <a
            href="/login"
            className="underline transition-colors hover:text-slate-500 dark:hover:text-slate-400"
          >
            管理员登录
          </a>
        </motion.p>
      </motion.div>
    </LensGlassRoot>
  );
}
